import React, { useEffect, useState } from "react";
import { Link, graphql, useStaticQuery } from "gatsby";
import classNames from "classnames";

type DraftboxSettings = {
  draftboxSettings: {
    content: {
      footer: {
        navigation: Array<{ label: string; url: string }>;
      };
    };
    identity: {
      copyright: string;
      siteName: string;
      socialLinks: {
        url: string;
        label: string;
        icon: [string, string];
      }[];
      removeDraftboxBranding: boolean;
    };
  };
  site: {
    siteMetadata: {
      siteUrl;
      pathPrefix;
    };
  };
};

const Footer: React.FC = () => {
  const draftboxSettingsData: DraftboxSettings = useStaticQuery(graphql`
    query {
      draftboxSettings {
        content {
          footer {
            navigation {
              label
              url
            }
          }
        }
        identity {
          copyright
          siteName
          socialLinks {
            url
            label
            icon
          }
          removeDraftboxBranding
        }
      }

      site {
        siteMetadata {
          siteUrl
          pathPrefix
        }
      }
    }
  `);

  const [hostName, setHostName] = useState(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setHostName(window.location.hostname);
    }
  }, []);

  const {
    draftboxSettings: {
      content: { footer: draftboxFooter },
      identity: { copyright, siteName, socialLinks, removeDraftboxBranding },
    },
    site: {
      siteMetadata: { siteUrl, pathPrefix },
    },
  } = draftboxSettingsData;

  return (
    <footer className="bg-gray-900 text-white">
      <div className="flex justify-between flex-wrap items-center py-4 px-4 border-b border-gray-800 container mx-auto">
        <div className="w-full lg:w-1/5 text-center lg:text-left mt-4 lg:mt-0">
          <span className="block md:inline-block md:mb-0 text-xs font-sansNormal text-white opacity-75">
            {copyright}
          </span>
        </div>
        <div
          className={classNames(
            "w-full flex flex-wrap px-6 items-center lg:w-3/5 mt-6 lg:mt-0",
            {
              "justify-center": socialLinks?.length > 0,
              "justify-center lg:justify-end": socialLinks?.length === 0,
            }
          )}
        >
          {draftboxFooter.navigation.map(({ label, url }, i) => {
            return !url.includes(".xml") &&
              (url.startsWith("/") || url.startsWith(siteUrl)) ? (
              <Link
                key={i}
                className="footer-navlink inline-block text-xs font-sansNormal py-4 lg:py-2 lg:mt-0 mx-3 lg:mx-3 text-white opacity-75 hover:opacity-100"
                to={url}
              >
                {label}
              </Link>
            ) : (
              <a
                key={i}
                href={
                  url.includes(".xml") && url.startsWith("/")
                    ? pathPrefix + url
                    : url
                }
                rel="noreferrer noopener"
                target={
                  url.includes(".xml") && url.startsWith("/")
                    ? "_self"
                    : "_blank"
                }
                className="footer-navlink inline-block text-xs font-sansNormal py-4 lg:py-2 lg:mt-0 mx-3 lg:mx-3 text-white opacity-75 hover:opacity-100"
              >
                {label}
              </a>
            );
          })}
        </div>

        {/* social Icons */}
        {socialLinks?.length > 0 && (
          <div className="flex flex-wrap lg:flex-no-wrap justify-center items-center lg:justify-end w-full lg:w-1/5 my-2 lg:my-0 mt-6 lg:mt-0 space-x-4">
            {socialLinks.map((social, index) => {
              return !social.url.includes(".xml") &&
                (social.url.startsWith("/") ||
                  social.url.startsWith(siteUrl)) ? (
                <Link key={index} to={social.url}>
                  <i
                    className={`${social.icon[0]} fa-${social.icon[1]} text-white opacity-75 hover:opacity-100`}
                  ></i>
                </Link>
              ) : (
                <a
                  key={index}
                  className="inline-block lg:mt-0"
                  rel="noreferrer noopener"
                  href={
                    social.url.includes(".xml") && social.url.startsWith("/")
                      ? pathPrefix + social.url
                      : social.url
                  }
                  target={
                    social.url.includes(".xml") && social.url.startsWith("/")
                      ? "_self"
                      : "_blank"
                  }
                  aria-label={social.label}
                >
                  <i
                    className={`${social.icon[0]} fa-${social.icon[1]} text-white opacity-75 hover:opacity-100`}
                  ></i>
                </a>
              );
            })}
          </div>
        )}
      </div>
      {!removeDraftboxBranding && (
        <div className="py-4 flex flex-col items-center justify-center">
          <a
            className="footer-navlink py-1 font-sansNormal text-xs text-white opacity-75 hover:opacity-100"
            href={`https://draftbox.co?ref=${hostName}`}
            rel="noreferrer noopener"
            target="_blank"
          >
            <span className="mr-1">❤️</span>
            <span className="">Published with Draftbox</span>
          </a>
        </div>
      )}
    </footer>
  );
};

export default Footer;
